<template>
  <div class="flex flex-col h-screen min-w-0 overflow-hidden overflow-x-hidden">
    <!-- 头部 - 横跨全宽 -->
    <div class="h-15 bg-blue-900 bg-opacity-85 flex justify-between items-center px-5 shadow-md z-50 text-blue-100">
      <!-- 左侧桥梁选择和水文气象显示 -->
      <div class="flex items-center space-x-4 nav">
        <div class="platform-title mr-5 flex items-center">
          <img src="../assets/logo.png" alt="桥卫士" style="height: 18px;" class="mr-3">
          <h3 class="text-white font-bold text-lg whitespace-nowrap">
            {{ isDataDashboardRoute ? '数据看板' : '桥梁防撞主动预警系统' }}
          </h3>
        </div>
        <!-- 桥梁树选择器 -->
        <bridge-tree-selector 
          :is-show-bridge-selector="isShowBridgeSelector"
          :selected-bridge-id="selectedBridgeId"
          :bridge-options="bridgeOptions"
          @bridge-selection-change="handleBridgeSelectionChange"
        />
        <!-- 水文气象实时显示 -->
        <hydrometeorological-indicator 
          :selected-bridge-id="selectedBridgeId"
          :is-show-hydrometeorological="isShowHydrometeorological"
          :refresh-interval="30000"
        />
        <!-- 可视化大屏入口 - 仅在首页显示 -->
        <div class="visualization-entrance" v-if="isHomepageRoute">
          <el-button 
            type="primary" 
            size="small" 
            icon="el-icon-data-line"
            @click="openDataVisualization"
            class="visualization-btn">
            可视化大屏
          </el-button>
          <el-button 
            type="success" 
            size="small" 
            icon="el-icon-data-board"
            @click="openDataDashboard"
            class="visualization-btn">
            统计分析
          </el-button>
          <el-button 
            type="warning" 
            size="small" 
            icon="el-icon-monitor"
            @click="openDutyMonitor"
            class="visualization-btn">
            值班监控
          </el-button>
          <!-- 离线地图切换按钮 - 仅管理员/值班室可见 -->
          <map-mode-switch />
          <!-- 实时视频入口 - 仅管理员/值班室可见；当前桥梁无视频时置灰 -->
          <el-tooltip
            v-if="canSeeVideo && isDutyRoute"
            :content="currentBridgeHasVideo ? '查看当前桥梁实时视频' : '当前桥梁未接入视频（NVR 未开放 RTSP）'"
            placement="bottom"
          >
            <span class="video-entrance">
              <el-button
                type="info"
                size="small"
                icon="el-icon-video-camera"
                :disabled="!currentBridgeHasVideo"
                @click="openVideoDialog"
                class="visualization-btn">
                实时视频
              </el-button>
            </span>
          </el-tooltip>
        </div>
      </div>
      
      <!-- 中间导航按钮区域 -->
      <div class="nav-center-container" v-if="navButtons.length > 0">
        <nav-buttons 
          :buttons="navButtons" 
          @button-click="handleNavButtonClick"
          class="nav-buttons-list"
        />
      </div>
      
      
      <!-- 右侧用户信息 -->
      <div class="flex items-center space-x-3">
        <!-- 首页图标 -->
        <el-tooltip content="回到首页" placement="bottom">
          <i 
            class="el-icon-s-home text-blue-100 text-lg cursor-pointer hover:text-white transition-colors duration-200"
            @click="$router.push('/')"
          ></i>
        </el-tooltip>
        
        <el-dropdown trigger="click">
          <span class="text-blue-100 cursor-pointer">
            <i class="el-icon-user"></i> {{ username }} <i class="el-icon-arrow-down el-icon--right"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item @click.native="$router.push('/change-password')">
              <i class="el-icon-key"></i> 修改密码
            </el-dropdown-item>
            <el-dropdown-item divided @click.native="handleLogout">
              <i class="el-icon-switch-button"></i> 退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
    
    <!-- 中间区域：左侧导航栏 + 页面内容 -->
    <div class="flex flex-1 min-w-0 overflow-hidden overflow-x-hidden">
      <!-- 左侧导航栏 -->
      <side-nav 
        v-if="!isDataDashboardRoute" 
        ref="sideNav"
        class="h-full"
        @collapse-changed="handleCollapseChanged"
      />
      
      <!-- 页面内容 -->
      <div class="flex-1 overflow-auto bg-gray-100 relative">
        <slot></slot>
      </div>
    </div>
    
    <!-- 底部 - 横跨全宽 -->
    <div v-if="!isDataDashboardRoute" class="h-10 bg-blue-900 bg-opacity-85 flex justify-between items-center text-blue-100 text-xs border-t border-opacity-20 border-blue-300 px-5">
      <!-- 左侧：收缩按钮 + 最小化面板列表 -->
      <div class="flex items-center gap-2">
        <!-- 收缩按钮 -->
        <div class="flex justify-center items-center text-blue-100 cursor-pointer hover:text-white hover:bg-blue-700 h-full px-4" style="height: 28px;" @click="toggleSideNav">
          <i :class="isSideNavCollapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'"></i>
        </div>
        <!-- 最小化面板列表 -->
        <div 
          v-for="panel in minimizedPanels" 
          :key="panel.panelId"
          class="minimized-panel-item flex items-center gap-2 px-3 py-1 rounded cursor-pointer hover:bg-blue-700 transition-colors"
          @click="restoreMinimizedPanel(panel)"
          :title="`点击恢复: ${panel.title}`"
        >
          <i v-if="panel.icon" :class="panel.icon" class="text-sm"></i>
          <span class="text-xs whitespace-nowrap">{{ panel.title }}</span>
        </div>
      </div>
      <!-- 中间版权信息 -->
      <p class="flex-1 text-center">© 2025 桥卫士</p>
      <!-- 右侧占位，保持布局平衡 -->
      <div class="w-12"></div>

    <video-dialog :visible.sync="videoDialogVisible" :bridge-id="videoBridgeId" :bridge-name="videoBridgeName" />
    </div>
  </div>
</template>

<script>
import layoutScript from './LayoutScript.js'
import SideNav from './SideNav.vue'
import NavButtons from './common/NavButtons.vue'
import HydrometeorologicalIndicator from './common/HydrometeorologicalIndicator.vue'
import BridgeTreeSelector from './common/BridgeTreeSelector.vue'
import MapModeSwitch from './common/MapModeSwitch.vue'
import VideoDialog from './video/VideoDialog.vue'
import { hasAdminAccess, getUserRoles } from '@/utils/permissionUtils'
import { getBridges } from '@/api/video'

export default {
  name: 'Layout',
  components: {
    SideNav,
    NavButtons,
    HydrometeorologicalIndicator,
    BridgeTreeSelector,
    MapModeSwitch,
    VideoDialog
  },
  mixins: [layoutScript],
  provide() {
    return {
      registerNavButtons: this.registerNavButtons,
      unregisterNavButtons: this.unregisterNavButtons,
      registerMinimizedPanel: this.registerMinimizedPanel,
      unregisterMinimizedPanel: this.unregisterMinimizedPanel
    }
  },
  data() {
    return {
      navButtons: [],
      isSideNavCollapsed: false,
      minimizedPanels: [], // 最小化面板列表
      videoDialogVisible: false,
      videoBridges: []
    }
  },
  computed: {
    isDashboardRoute() {
      // 检查当前路径是否是dashboard
      return this.$route.path === '/dashboard' || this.$route.path === '/dashboard/';
    },
    isDataDashboardRoute() {
      // Analytics页面现在使用独立的AnalyticsLayout
      return false;
    },
    // 实时视频：仅管理员/值班室可见
    canSeeVideo() {
      const roles = (this.roles && this.roles.length) ? this.roles : getUserRoles()
      return roles.includes('ADMIN') || roles.includes('MONITOR') || hasAdminAccess()
    },
    // 当前桥梁是否有可播视频（其 NVR 已开放 RTSP）
    currentBridgeHasVideo() {
      const id = this.selectedBridgeId;
      if (!id) return false;
      const b = this.videoBridges.find(x => String(x.bridgeId) === String(id));
      return !!(b && Number(b.pullable) > 0);
    },
    videoBridgeId() {
      return this.selectedBridgeId || (this.selectedBridge && this.selectedBridge.id) || '';
    },
    videoBridgeName() {
      return (this.selectedBridge && this.selectedBridge.name) || '';
    },
    isHomepageRoute() {
      // 首页与值班监控页都显示顶部快捷按钮
      return this.$route.path === '/' || this.$route.path === '/work/monitor';
    },
    isDutyRoute() {
      return this.$route.path === '/work/monitor';
    },
    isShowBridgeSelector() {
      // 检查当前路径是否展示桥梁选择器
      return this.$route.path === '/' || this.$route.path === '/equipment-all' || this.$route.path === '/alert/bridge-alerts' || this.$route.path === '/bridge-record' || this.$route.path === '/work/monitor';
    },
    isShowHydrometeorological() {
      // 检查当前路径是否展示水文气象控件
      // 设备管理页面(/equipment-all)不显示这些控件，因为设备管理主要关注设备状态而非环境数据
      const showOnRoutes = ['/', '/alert/bridge-alerts', '/bridge-record'];
      const hideOnRoutes = ['/equipment-all'];
      
      return showOnRoutes.includes(this.$route.path) && !hideOnRoutes.includes(this.$route.path);
    },
    
  },
  mounted() {
    // 预取视频桥列表（用于判断当前桥梁是否有可播视频）
    this.loadVideoBridges();
    // 初始化侧边栏折叠状态
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsed !== null) {
      this.isSideNavCollapsed = savedCollapsed === 'true';
    }
  },
  methods: {
    // 提供给子组件注册按钮的方法
    registerNavButtons(buttons) {
      this.navButtons = buttons;
    },
    // 提供给子组件注销按钮的方法
    unregisterNavButtons() {
      this.navButtons = [];
    },
    // 处理导航按钮点击事件
    handleNavButtonClick(button) {
      if (button && typeof button.handler === 'function') {
        button.handler();
      }
    },
    // 读取视频桥列表（判断"当前桥梁是否有视频"）
    async loadVideoBridges() {
      try {
        const res = await getBridges();
        this.videoBridges = (res && res.data) || [];
      } catch (e) {
        this.videoBridges = [];
      }
    },
    // 打开实时视频弹窗
    openVideoDialog() {
      if (!this.videoBridgeId) {
        this.$message.warning('请先选择桥梁');
        return;
      }
      this.loadVideoBridges();
      this.videoDialogVisible = true;
    },
    // 打开可视化大屏
    openDataVisualization() {
      // 从localStorage获取选中的桥梁信息
      const selectedBridgeStr = localStorage.getItem('selectedBridge');
      
      if (!selectedBridgeStr) {
        this.$message.warning('请先选择桥梁');
        return;
      }
      
      try {
        const selectedBridge = JSON.parse(selectedBridgeStr);
        const bridgeId = selectedBridge.id;
        
        if (!bridgeId) {
          this.$message.warning('桥梁信息不完整，请重新选择');
          return;
        }
        
        // 在新窗口中打开可视化大屏
        const url = this.$router.resolve({ 
          name: 'DataVisualization'
        }).href;
        window.open(url, '_blank');
        
      } catch (error) {
        console.error('解析桥梁信息失败:', error);
        this.$message.error('桥梁信息格式错误，请重新选择');
      }
    },
    
    // 打开数据看板
    openDataDashboard() {
      // 在新窗口中打开数据看板
      const url = this.$router.resolve({ 
        path: '/analytics/dashboard'
      }).href;
      window.open(url, '_blank');
    },
    // 打开值班监控
    openDutyMonitor() {
      const url = this.$router.resolve({
        path: '/work/monitor'
      }).href;
      window.open(url, '_blank');
    },
    // 切换侧边栏折叠状态
    toggleSideNav() {
      if (this.$refs.sideNav) {
        this.$refs.sideNav.toggleCollapse();
      }
    },
    // 处理侧边栏折叠状态变化
    handleCollapseChanged(isCollapsed) {
      this.isSideNavCollapsed = isCollapsed;
    },
    // 注册最小化面板
    registerMinimizedPanel(panelInfo) {
      // 检查是否已存在
      const existingIndex = this.minimizedPanels.findIndex(p => p.panelId === panelInfo.panelId);
      if (existingIndex === -1) {
        this.minimizedPanels.push(panelInfo);
      }
    },
    // 注销最小化面板
    unregisterMinimizedPanel(panelId) {
      const index = this.minimizedPanels.findIndex(p => p.panelId === panelId);
      if (index !== -1) {
        this.minimizedPanels.splice(index, 1);
      }
    },
    // 恢复最小化面板
    restoreMinimizedPanel(panelInfo) {
      if (panelInfo && panelInfo.restoreHandler) {
        panelInfo.restoreHandler();
      }
    }
  }
}
</script>

<style scoped>

/* 中间导航容器样式 */
.nav-center-container {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  max-width: 800px;
  z-index: 10;
}

.nav-buttons-list {
  display: flex;
  align-items: center;
  gap: 8px;
}

.function-title {
  font-size: 14px;
  letter-spacing: 0.5px;
  font-weight: 500;
}

html, body {
  overflow-x: hidden !important;
  margin: 0;
  padding: 0;
  height: 100%;
}

/* 确保边栏和内容区域占满整个屏幕 */
.h-screen {
  height: 100vh;
  min-height: 100vh;
}


/* 可视化大屏入口样式 */
.visualization-entrance {
  margin-left: 12px;
  display: flex;
  gap: 8px;
}

.visualization-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.visualization-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.visualization-btn:active {
  transform: translateY(0);
}

/* 数据看板按钮样式 */
.dashboard-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(79, 172, 254, 0.3);
}

.dashboard-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.4);
  background: linear-gradient(135deg, #00f2fe 0%, #4facfe 100%);
}

.dashboard-btn:active {
  transform: translateY(0);
}

.nav {
   padding: 12px 0;
}


/* 统计分析下拉菜单样式 */

/* 最小化面板样式 */
.minimized-panel-item {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.minimized-panel-item:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}
</style>